import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface StarlineSession {
  id: number;
  market: number;
  market_name: string;
  session_date: string;
  session_time: string;
  betting_opens_at: string;
  betting_closes_at: string;
  status: string;
  result_pana: string | null;
  result_digit: string | null;
  result_display: string | null;
  result_declared_at: string | null;
  settled_at: string | null;
  is_betting_open: boolean;
  created_at: string;
  updated_at: string;
}

interface FetchStarlineSessionsParams {
  marketId?: number;
  sessionDate?: string;
  status?: string;
  openOnly?: boolean;
}

interface StarlineMarketState {
  sessions: StarlineSession[];
  count: number;
  loading: boolean;
  error: string | null;
  filters: {
    marketId: number;
    sessionDate: string;
    status: string;
    openOnly: boolean;
  };
}

const getTodayDate = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60 * 1000)
    .toISOString()
    .split('T')[0];
};

const initialState: StarlineMarketState = {
  sessions: [],
  count: 0,
  loading: false,
  error: null,
  filters: {
    marketId: 33,
    sessionDate: getTodayDate(),
    status: '',
    openOnly: false,
  },
};

const extractErrorMessage = (error: any): string => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.message ||
    'Unable to load Starline sessions.'
  );
};

export const fetchStarlineSessions = createAsyncThunk<
  { sessions: StarlineSession[]; count: number },
  FetchStarlineSessionsParams | undefined,
  { rejectValue: string }
>(
  'starlineMarket/fetchStarlineSessions',
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();

      query.set('market_id', String(params?.marketId ?? 33));

      if (params?.sessionDate) {
        query.set('session_date', params.sessionDate);
      }

      if (params?.status) {
        query.set('status', params.status);
      }

      if (params?.openOnly) {
        query.set('open_only', 'true');
      }

      const response = await api.get(
        `/starline/sessions/?${query.toString()}`
      );

      return {
        sessions: Array.isArray(response.data?.sessions)
          ? response.data.sessions
          : [],
        count: Number(response.data?.count || 0),
      };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

const starlineMarketSlice = createSlice({
  name: 'starlineMarket',
  initialState,
  reducers: {
    setStarlineFilters: (
      state,
      action: PayloadAction<Partial<StarlineMarketState['filters']>>
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    clearStarlineMarketError: (state) => {
      state.error = null;
    },
    resetStarlineMarket: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStarlineSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStarlineSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload.sessions;
        state.count = action.payload.count;
      })
      .addCase(fetchStarlineSessions.rejected, (state, action) => {
        state.loading = false;
        state.sessions = [];
        state.count = 0;
        state.error =
          action.payload || 'Unable to load Starline sessions.';
      });
  },
});

export const {
  setStarlineFilters,
  clearStarlineMarketError,
  resetStarlineMarket,
} = starlineMarketSlice.actions;

export default starlineMarketSlice.reducer;
